import { getPopularPreviewRecipes, PREFERENCE_IDS } from "@chef/constants";
import { isTastePreference } from "@chef/helpers";
import { isEmptyArray, isNotEmptyArray } from "@chef/utils/array";

import {
  usePreferencesQuery,
  usePreselectorPreviewQuery,
  useRecipesByIdsQuery,
} from "../";

interface UsePreviewRecipesProps {
  conceptPreferenceIds: string[];
  tastePreferenceIds: string[];
  week: number;
  year: number;
  portions: number;
}

export const usePreviewRecipes = ({
  conceptPreferenceIds,
  tastePreferenceIds,
  week,
  year,
  portions,
}: UsePreviewRecipesProps) => {
  const { data: preferencesQuery } = usePreferencesQuery();

  const tastePreferences = preferencesQuery?.preferences
    .filter(isTastePreference)
    .filter((taste) => tastePreferenceIds.includes(taste.preferenceId))
    .map(({ preferenceId, isAllergen }) => ({ preferenceId, isAllergen }));

  // Fetch popular recipes on the first load
  const popularRecipes = getPopularPreviewRecipes({
    week,
    year,
    conceptPreferenceIds,
    tastePreferenceIds,
  });

  const popularRecipesSelected = isNotEmptyArray(popularRecipes);

  const { data: previewQuery, isFetching: isFetchingPreview } =
    usePreselectorPreviewQuery(
      {
        week,
        year,
        portions,
        attributeIds: isEmptyArray(conceptPreferenceIds)
          ? [PREFERENCE_IDS.CHEF_CHOICE]
          : conceptPreferenceIds,
        tastePreferences,
      },
      {
        skip: popularRecipesSelected,
      },
    );

  const recipeIds = previewQuery?.preselectorPreview.recipeIds || [];

  const { data: recipesByIdsQuery, isFetching: isFetchingRecipes } =
    useRecipesByIdsQuery(
      {
        recipeIds,
      },
      {
        skip: isEmptyArray(recipeIds) || !recipeIds || popularRecipesSelected,
      },
    );

  const isLoading = isFetchingPreview || isFetchingRecipes;

  // If the selected preferences match the pre-generated popular recipes, return them.
  if (popularRecipesSelected) {
    return { recipes: popularRecipes, isLoading: false };
  }

  return { recipes: recipesByIdsQuery?.recipesByIds ?? [], isLoading };
};
